import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from '@cpd-app/environment';

const COOKIE_ID = 'cookie-consent';
/**
 * P-1. The class on OUR injected agreement checkbox.
 *
 * Deliberately not the vendor's `cc-nb-ec-check`: TermsFeed enumerates elements
 * with that class as consent CATEGORIES and reads a category off each. Ours is
 * not a category, and wearing that class made the vendor record one named
 * "undefined".
 */
const CONSENT_CHECK_CLASS = 'cpd-nb-consent-check';
const COOKIE_CONSENT_SCRIPT_URL =
  'https://www.termsfeed.com/public/cookie-consent/4.2.0/cookie-consent.js';

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private renderer: Renderer2;

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  public embedCookies(): void {
    this.embedNoscript();
    this.loadScript(COOKIE_CONSENT_SCRIPT_URL).subscribe({
      next: () => {
        if (!this.document.getElementById(`${COOKIE_ID}-script`)) {
          const script = this.renderer.createElement('script');
          const scriptText = this.renderer.createText(
            `cookieconsent.run({"notice_banner_type":"simple","consent_type":"express","palette":"dark","language":"en","page_load_consent_levels":["strictly-necessary"],"notice_banner_reject_button_hide":false,"preferences_center_close_button_hide":false,"page_refresh_confirmation_buttons":false,"website_name":"Career Path DECIDE","website_privacy_policy_url":"${environment.clientUrl}/about/privacy","website_terms_of_service_url":"${environment.clientUrl}/about/terms-of-use","notice_banner_title":"We use cookies","notice_banner_description":"This website uses cookies and other tracking technologies to improve your browsing experience, measure your interest in our services, and personalize marketing interactions."});`
          );
          this.renderer.setAttribute(script, 'id', `${COOKIE_ID}-script`);
          this.renderer.appendChild(script, scriptText);
          this.renderer.setAttribute(script, 'type', 'text/javascript');
          this.renderer.setAttribute(script, 'charset', 'UTF-8');
          this.renderer.appendChild(this.document.head, script);
        }

        this.wireAgreeButtonValidation();
      }
    });
  }

  private wireAgreeButtonValidation(): void {
    const RADIO_ID = `${COOKIE_ID}-radio`;
    this.waitForBanner().subscribe((banner) => {
      const agreeBtn = banner.querySelector<HTMLButtonElement>('.cc-nb-okagree');
      const rejectBtn = banner.querySelector<HTMLButtonElement>('.cc-nb-reject');
      const changePreferencesBtn = banner.querySelector<HTMLButtonElement>('.cc-nb-changep');
      const buttonsContainer = banner.querySelector<HTMLElement>('.cc-nb-buttons-container');

      if (!agreeBtn || !buttonsContainer) {
        return;
      }
      // P-1. Two different checkboxes can end up here and they are NOT
      // interchangeable:
      //
      //   input.cc-nb-ec-check     belongs to TermsFeed. The vendor script
      //                            enumerates every element with this class and
      //                            reads a consent CATEGORY off each one. It
      //                            renders these itself when configured to.
      //   input#cookie-consent-radio  is ours, injected below, and is a plain
      //                            "I have read and agree" gate on the buttons.
      //                            It is not a category and has never been one.
      //
      // Ours used to carry the vendor's class. The vendor therefore counted it
      // as a category checkbox, found no category attribute on it, and wrote a
      // category literally named "undefined" into the stored consent --
      // {"strictly-necessary":true, ..., "undefined":false} -- while throwing
      // about sixteen times per page load. The banner still rendered and
      // consent was still recorded, so this was a malformed record rather than
      // a broken banner. For a federal contract a malformed consent record is
      // still an audit finding.
      //
      // The lookup below still asks for the vendor's class FIRST, because the
      // question it is answering is "did TermsFeed already give us an express
      // consent checkbox to wire up?" -- if it did, we must use theirs and not
      // inject a second one. Only when it did not do we fall back to ours.
      let checkbox =
        banner.querySelector<HTMLInputElement>('input.cc-nb-ec-check') ??
        this.document.getElementById(RADIO_ID) as HTMLInputElement | null;

      if (!checkbox) {
        const mainContainer = banner.querySelector<HTMLElement>('.cc-nb-main-container') ?? banner;

        const label = this.renderer.createElement('label') as HTMLLabelElement;
        this.renderer.setAttribute(label, 'class', 'cc-nb-express-consent-agreement');

        checkbox = this.renderer.createElement('input') as HTMLInputElement;
        this.renderer.setProperty(checkbox, 'type', 'checkbox');
        this.renderer.setAttribute(checkbox, 'id', RADIO_ID);
        // Our own class. Reusing the vendor's is what caused P-1; the styling
        // in scss/vendors/termsfeed/_cookie-consent.scss matches both.
        this.renderer.setAttribute(checkbox, 'class', CONSENT_CHECK_CLASS);

        const privacyLink = this.renderer.createElement('a') as HTMLAnchorElement;
        this.renderer.setAttribute(privacyLink, 'href', `${environment.clientUrl}/about/privacy`);
        this.renderer.setAttribute(privacyLink, 'target', '_blank');
        this.renderer.setAttribute(privacyLink, 'rel', 'noopener noreferrer');
        this.renderer.appendChild(privacyLink, this.renderer.createText('Privacy Policy'));

        const termsLink = this.renderer.createElement('a') as HTMLAnchorElement;
        this.renderer.setAttribute(termsLink, 'href', `${environment.clientUrl}/about/terms-of-use`);
        this.renderer.setAttribute(termsLink, 'target', '_blank');
        this.renderer.setAttribute(termsLink, 'rel', 'noopener noreferrer');
        this.renderer.appendChild(termsLink, this.renderer.createText('Terms of Use'));

        this.renderer.appendChild(label, checkbox);
        this.renderer.appendChild(label, this.renderer.createText('I have read and agree to the'));
        this.renderer.appendChild(label, privacyLink);
        this.renderer.appendChild(label, this.renderer.createText('and'));
        this.renderer.appendChild(label, termsLink);
        this.renderer.appendChild(label, this.renderer.createText('.'));

        this.renderer.insertBefore(mainContainer, label, buttonsContainer);
      }

      if (checkbox) {
        // rejectBtn and changePreferencesBtn were dereferenced unguarded while
        // only agreeBtn was null-checked above. Both are configurable away in
        // the cookieconsent.run() options (notice_banner_reject_button_hide,
        // preferences_center_close_button_hide), so turning either off would
        // have thrown here and left every button disabled -- an unusable
        // banner. The gate is on the agree button; the others follow it if
        // they exist.
        const gated = [agreeBtn, rejectBtn, changePreferencesBtn].filter(
          (button): button is HTMLButtonElement => !!button
        );
        const sync = () => {
          for (const button of gated) {
            button.disabled = !checkbox.checked;
          }
        };
        sync();
        checkbox.addEventListener('change', sync);
      }
    });
  }

  private waitForBanner(): Observable<HTMLElement> {
    return new Observable<HTMLElement>((subscriber) => {
      const existing = this.document.querySelector<HTMLElement>('.termsfeed-com---nb');
      if (existing?.querySelector('.cc-nb-buttons-container')) {
        subscriber.next(existing);
        subscriber.complete();
        return;
      }

      const observer = new MutationObserver(() => {
        const banner = this.document.querySelector<HTMLElement>('.termsfeed-com---nb');
        if (banner?.querySelector('.cc-nb-buttons-container')) {
          observer.disconnect();
          subscriber.next(banner);
          subscriber.complete();
        }
      });

      observer.observe(this.document.documentElement, { childList: true, subtree: true });

      return () => observer.disconnect();
    }).pipe(filter(Boolean), take(1));
  }

  private embedNoscript(): void {
    if (this.document.getElementById(`${COOKIE_ID}-noscript`)) {
      return;
    }
    const noscript = this.renderer.createElement('noscript');
    this.renderer.setAttribute(noscript, 'id', `${COOKIE_ID}-noscript`);
    const noscriptText1 = this.renderer.createText(
      'Free cookie consent management tool by '
    );
    const noscriptLink = this.renderer.createElement('a');
    this.renderer.setAttribute(
      noscriptLink,
      'href',
      'https://www.termsfeed.com/'
    );
    const linkText = this.renderer.createText('TermsFeed Generator');
    this.renderer.appendChild(noscriptLink, linkText);
    this.renderer.appendChild(noscript, noscriptText1);
    this.renderer.appendChild(noscript, noscriptLink);
    this.renderer.appendChild(this.document.head, noscript);
  }

  private loadScript(src: string): Observable<void> {
    return new Observable((subscriber) => {
      if (this.document.getElementById(COOKIE_ID)) {
        subscriber.next();
        subscriber.complete();
      } else {
        const cookieScript = this.renderer.createElement('script');
        this.renderer.setAttribute(cookieScript, 'src', src);
        this.renderer.setAttribute(cookieScript, 'id', COOKIE_ID);
        this.renderer.setAttribute(cookieScript, 'charset', 'UTF-8');

        this.renderer.listen(cookieScript, 'load', () => {
          subscriber.next();
          subscriber.complete();
        });

        this.renderer.listen(cookieScript, 'error', () => {
          subscriber.error(new Error(`Cant load ${COOKIE_ID}`));
        });

        this.renderer.appendChild(this.document.head, cookieScript);
      }
    });
  }
}
